/**
 * TvPageBar — eyebrow + Inter Tight title + right-aligned actions.
 *
 *   <TvPageBar
 *     eyebrow="Marketing & Sales · Today · Fri, May 30"
 *     title="My day"
 *     actions={
 *       <>
 *         <TvButton variant="ghost" icon={LayoutGrid}>Modules</TvButton>
 *         <TvButton variant="ink" icon={Zap}>Quick actions</TvButton>
 *       </>
 *     }
 *   />
 *
 * Spec (from design_handoff_platform_redesign/dashboard.jsx):
 *   13px 22px padding · surface bg · 1px border-bottom · h ≈ 56 · gap 12
 *   eyebrow mono UPPERCASE 10px · title Inter Tight 18px 600 tracking -0.015em
 */
import React from 'react';
import { tvTokens } from './tokens';
import { TvEyebrow } from './TvEyebrow';
import { useTabbedChrome } from '@/components/weavein/TabbedChrome';

interface TvPageBarProps {
  /** Optional eyebrow line above the title. */
  eyebrow?: React.ReactNode;
  /** Page title — rendered Inter Tight 18 / 600. */
  title: React.ReactNode;
  /** Right-aligned action area — typically TvButtons. */
  actions?: React.ReactNode;
  /** Override the wrapper style if a specific page needs a tweak. */
  style?: React.CSSProperties;
}

export function TvPageBar({ eyebrow, title, actions, style }: TvPageBarProps) {
  // Under a tab strip the tab names the page: drop the title, keep the eyebrow
  // (often a date) and actions, and drop the bar when nothing is left.
  const tabbed = useTabbedChrome();
  if (tabbed && eyebrow === undefined && !actions) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 22px',
        background: tvTokens.surface,
        borderBottom: `1px solid ${tvTokens.border}`,
        ...style,
      }}
    >
      <div>
        {eyebrow !== undefined && <TvEyebrow>{eyebrow}</TvEyebrow>}
        {!tabbed && <div
          style={{
            fontFamily: tvTokens.fontDisplay,
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: '-0.015em',
            color: tvTokens.ink,
          }}
        >
          {title}
        </div>}
      </div>
      <span style={{ flex: 1 }} />
      {actions}
    </div>
  );
}
